import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import teacherAssignmentsService from '../../services/teacherAssignmentsService';
import SubmissionGradingPanel from './SubmissionGradingPanel';
import DialogModal from '../../components/DialogModal';
import {
  FormInput,
  FormSelect,
} from '../../components/finance';
import { Plus, Pencil, Trash, ChartBar, X, CheckCircle, Upload, Warning, DownloadSimple, FolderOpen } from '@phosphor-icons/react';
import './TeacherAssignmentsSection.css';

function TeacherAssignmentsSection({ taughtClasses }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showGradingPanel, setShowGradingPanel] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [modalTab, setModalTab] = useState('basic');
  const [confirmDeleteAssignmentId, setConfirmDeleteAssignmentId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [formData, setFormData] = useState({
    class_id: '',
    subject_id: '',
    assignment_type: 'classwork',
    title: '',
    description: '',
    due_date: '',
    total_points: 100,
    instructions: '',
  });

  const [questions, setQuestions] = useState([]);
  const [gradingSettings, setGradingSettings] = useState({
    allow_auto_grade: true,
    question_types: ['essay', 'multipleChoice', 'shortAnswer'],
    rubric_enabled: false,
    rubric_criteria: [],
  });

  // Load assignments
  const loadAssignments = () => {
    setLoading(true);
    teacherAssignmentsService.listMyAssignments()
      .then(data => {
        setAssignments(data.items || []);
      })
      .catch(err => {
        console.error(err);
        // Check if it's a staff profile not found error
        if (err.response?.status === 404 && err.response?.data?.detail?.includes('Staff profile')) {
          toast.error('Staff profile not found. Please ensure your portal credentials have been generated by the admin.');
        } else {
          toast.error('Failed to load assignments');
        }
      })
      .finally(() => setLoading(false));
  };

  // Log taught classes for debugging
  useEffect(() => {
    console.log('Taught Classes Available:', taughtClasses);
    if (taughtClasses.length === 0) {
      console.warn('No taught classes available - user may not be assigned to any classes');
    }
  }, [taughtClasses]);

  // Initial load
  useEffect(() => {
    loadAssignments();
  }, []);

  // Handle create assignment
  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    
    if (!formData.class_id || !formData.title || !formData.due_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Verify teacher is assigned to this class
    const assignedClass = taughtClasses.find(c => c.class_id === formData.class_id);
    if (!assignedClass) {
      toast.error('You are not assigned to teach this class. Please select a class from your taught classes.');
      return;
    }

    try {
      const payload = {
        class_id: formData.class_id,
        subject_id: assignedClass.subject_id,
        assignment_type: formData.assignment_type || 'classwork',
        academic_term_id: assignedClass.academic_term_id, // Use the actual term from teacher assignment
        title: formData.title,
        description: formData.description,
        instructions: formData.instructions,
        due_date: formData.due_date,
        points_possible: parseFloat(formData.total_points) || 100,
        questions: questions, // Include questions from CSV
        grading_settings: gradingSettings, // Include grading configuration
      };

      console.log('Creating assignment with payload:', payload);

      if (selectedAssignment) {
        const assignmentId = selectedAssignment.id;
        console.log('Updating assignment with ID:', assignmentId);
        const response = await teacherAssignmentsService.updateAssignment(assignmentId, payload);
        console.log('Update response:', response);
        toast.success('Assignment updated successfully');
      } else {
        await teacherAssignmentsService.createAssignment(payload);
        toast.success('Assignment created successfully');
      }

      setShowCreateModal(false);
      resetForm();
      loadAssignments();
    } catch (err) {
      console.error('Assignment creation error:', err);
      const errorMsg = err.response?.data?.detail;
      
      if (err.response?.status === 403) {
        toast.error('You do not have permission to create assignments for this class.');
      } else if (err.response?.status === 404) {
        toast.error('The class or subject was not found. Please try again.');
      } else if (errorMsg?.includes('not assigned')) {
        toast.error('You are not assigned to teach this class/subject combination. Contact your admin.');
      } else {
        toast.error(errorMsg || 'Failed to save assignment');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      class_id: '',
      subject_id: '',
      assignment_type: 'classwork',
      title: '',
      description: '',
      due_date: '',
      total_points: 100,
      instructions: '',
    });
    setQuestions([]);
    setGradingSettings({
      allow_auto_grade: true,
      question_types: ['essay', 'multipleChoice', 'shortAnswer'],
      rubric_enabled: false,
      rubric_criteria: [],
    });
    setModalTab('basic');
    setSelectedAssignment(null);
  };

  const handleEditAssignment = async (assignment) => {
    try {
      // Fetch full assignment details with questions from API
      const response = await teacherAssignmentsService.getAssignment(assignment.id);
      const fullAssignment = response.data?.assignment || response.assignment;
      
      console.log('Loaded assignment details:', fullAssignment);
      
      setFormData({
        class_id: fullAssignment.class_id || '',
        subject_id: fullAssignment.subject_id || '',
        assignment_type: fullAssignment.assignment_type || 'classwork',
        title: fullAssignment.title || '',
        description: fullAssignment.description || '',
        due_date: fullAssignment.due_date ? fullAssignment.due_date.split('T')[0] : '',
        total_points: fullAssignment.total_points || 100,
        instructions: fullAssignment.instructions || '',
      });
      
      // Load questions with proper structure
      if (fullAssignment.questions && fullAssignment.questions.length > 0) {
        const formattedQuestions = fullAssignment.questions.map(q => ({
          id: q.id,
          question: q.question,
          type: q.type,
          answer: q.answer,
          points: q.points,
          options: q.options || [],
        }));
        console.log('Loaded questions:', formattedQuestions);
        setQuestions(formattedQuestions);
      } else {
        setQuestions([]);
      }
      
      // Load grading settings if available
      if (fullAssignment.grading_settings) {
        setGradingSettings(fullAssignment.grading_settings);
      }
      
      setSelectedAssignment(fullAssignment);
      setShowCreateModal(true);
      setModalTab('basic');
    } catch (err) {
      console.error('Error loading assignment:', err);
      toast.error('Failed to load assignment details');
    }
  };

  const handleDeleteAssignment = (assignmentId) => {
    setConfirmDeleteAssignmentId(assignmentId);
  };

  const confirmDeleteAssignment = async () => {
    if (!confirmDeleteAssignmentId) return;

    setConfirmLoading(true);
    try {
      console.log('Deleting assignment with ID:', confirmDeleteAssignmentId);
      const response = await teacherAssignmentsService.deleteAssignment(confirmDeleteAssignmentId);
      console.log('Delete response:', response);
      toast.success('Assignment deleted successfully');
      loadAssignments();
    } catch (err) {
      console.error('Delete error:', err);
      console.error('Error response:', err.response);
      if (err.response?.status === 404 && err.response?.data?.detail?.includes('Staff profile')) {
        toast.error('Staff profile not found. Please ensure your portal credentials have been generated by the admin.');
      } else if (err.response?.status === 403) {
        toast.error(err.response?.data?.detail || 'You can only delete your own assignments');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to delete assignment');
      }
    } finally {
      setConfirmLoading(false);
      setConfirmDeleteAssignmentId(null);
    }
  };

  const handleViewStats = async (assignmentId) => {
    try {
      const data = await teacherAssignmentsService.getStatistics(assignmentId);
      setStats(data.statistics || {});
      setShowStatsModal(true);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 404 && err.response?.data?.detail?.includes('Staff profile')) {
        toast.error('Staff profile not found. Please ensure your portal credentials have been generated by the admin.');
      } else {
        toast.error('Failed to load statistics');
      }
    }
  };

  const handlePublishAssignment = async (assignmentId) => {
    try {
      const response = await teacherAssignmentsService.publishAssignment(assignmentId);
      toast.success('Assignment published successfully! Students can now see it.');
      loadAssignments();
    } catch (err) {
      console.error('Publish error:', err);
      if (err.response?.status === 404 && err.response?.data?.detail?.includes('Staff profile')) {
        toast.error('Staff profile not found. Please ensure your portal credentials have been generated by the admin.');
      } else if (err.response?.status === 400) {
        toast.error(err.response?.data?.detail || 'Only draft assignments can be published');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to publish assignment');
      }
    }
  };

  const handleGradeSubmissions = async (assignmentId) => {
    try {
      // Find assignment details
      const assignment = assignments.find(a => a.id === assignmentId);
      if (!assignment) {
        toast.error('Assignment not found');
        return;
      }

      // Load submissions
      const data = await teacherAssignmentsService.getSubmissions(assignmentId);
      let submissionsList = Array.isArray(data) ? data : data.items || [];

      // Filter out students who haven't submitted yet.
      // Backend uses lowercase submission status values like 'not_submitted'.
      submissionsList = submissionsList.filter(
        submission => submission.status && submission.status.toLowerCase() !== 'not_submitted'
      );

      if (submissionsList.length === 0) {
        toast.info('No submissions yet for this assignment');
        return;
      }

      setSubmissions(submissionsList);
      setSelectedAssignment(assignment);
      setShowGradingPanel(true);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 404 && err.response?.data?.detail?.includes('Staff profile')) {
        toast.error('Staff profile not found. Please ensure your portal credentials have been generated by the admin.');
      } else {
        toast.error('Failed to load submissions');
      }
    }
  };

  // CSV Example Template
  const csvExampleTemplate = `question,type,options,answer
What are the three main states of matter? Explain each one.,essay,,Solid has fixed shape and volume. Liquid has fixed volume but takes shape of container. Gas has no fixed shape or volume.
Explain the process of photosynthesis and its importance to life on Earth.,essay,,Plants convert light energy into chemical energy. It produces oxygen and glucose. Essential for life as it provides oxygen and food chain base.
Discuss the causes and effects of climate change.,essay,,Causes include greenhouse gases from burning fossil fuels. Effects include rising temperatures and sea level rise. Extreme weather events and ecosystem disruption occur.
Which of these is NOT a renewable energy source?,multipleChoice,A) Solar|B) Wind|C) Coal|D) Hydroelectric,C
What is the capital of France?,multipleChoice,A) London|B) Paris|C) Berlin|D) Madrid,B
Which scientist formulated the theory of evolution by natural selection?,multipleChoice,A) Charles Darwin|B) Albert Einstein|C) Marie Curie|D) Isaac Newton,A
What is the largest planet in our solar system?,multipleChoice,A) Saturn|B) Neptune|C) Jupiter|D) Uranus,C
Which country is the most populated in Africa?,multipleChoice,A) Egypt|B) Nigeria|C) Ethiopia|D) Democratic Republic of Congo,B
How many bones are in the adult human body?,multipleChoice,A) 186|B) 206|C) 226|D) 246,B
What is the chemical symbol for gold?,multipleChoice,A) Go|B) Gd|C) Au|D) Ag,C
Define the term "metabolism",shortAnswer,,The sum of all chemical processes that occur in living organisms.
What year did World War II end?,shortAnswer,,1945
How do you spell the word for a person who studies insects?,shortAnswer,,Entomologist
What is the formula for calculating the area of a circle?,shortAnswer,,A = πr² or pi times radius squared
What does DNA stand for?,shortAnswer,,Deoxyribonucleic acid
Who wrote the novel "Pride and Prejudice"?,shortAnswer,,Jane Austen
What is the capital of Japan?,shortAnswer,,Tokyo
How many continents are there?,shortAnswer,,7
What is the smallest unit of life?,shortAnswer,,Cell
Define photosynthesis in one sentence.,shortAnswer,,The process by which plants convert light energy into chemical energy using carbon dioxide and water.
Match the African country to its largest city,matching,Egypt=Cairo|Nigeria=Lagos|South Africa=Johannesburg|Kenya=Nairobi,Egypt=Cairo|Nigeria=Lagos|South Africa=Johannesburg|Kenya=Nairobi
Match the literary device to its definition,matching,Metaphor=Direct comparison without like or as|Simile=Comparison using like or as|Alliteration=Repetition of initial consonant sounds,Metaphor=Direct comparison without like or as|Simile=Comparison using like or as|Alliteration=Repetition of initial consonant sounds
Match the mathematical term to its definition,matching,Circumference=Distance around a circle|Diameter=A straight line through the center|Radius=Distance from center to edge,Circumference=Distance around a circle|Diameter=A straight line through the center|Radius=Distance from center to edge
Match the historical period to its description,matching,Renaissance=Rebirth of learning in Europe|Medieval=Middle Ages|Industrial Revolution=Rise of factories and machines,Renaissance=Rebirth of learning in Europe|Medieval=Middle Ages|Industrial Revolution=Rise of factories and machines
Match the scientist to their discovery,matching,Isaac Newton=Law of Gravity|Albert Einstein=Theory of Relativity|Marie Curie=Radioactivity,Isaac Newton=Law of Gravity|Albert Einstein=Theory of Relativity|Marie Curie=Radioactivity
Match the organ to its function,matching,Heart=Pumps blood|Lungs=Processes oxygen|Brain=Controls body,Heart=Pumps blood|Lungs=Processes oxygen|Brain=Controls body
Explain the water cycle and its importance to Earth's ecosystem.,essay,,Answer should include evaporation and condensation and precipitation. Water cycle regulates climate. It distributes water across Earth.
What is 7 × 8?,multipleChoice,A) 56|B) 54|C) 58|D) 50,A
What is the main function of mitochondria in a cell?,shortAnswer,,To produce energy in the form of ATP through cellular respiration.
Describe the three branches of government and their roles.,essay,,Legislation creates laws. Executive enforces laws. Judicial interprets laws. System of checks and balances prevents any single branch from gaining too much power.
Which of the following best describes condensation?,multipleChoice,A) Water vapor turning into liquid|B) Liquid turning into gas|C) Ice melting|D) Water freezing,A
What is 15 ÷ 3?,shortAnswer,,5
Match the continent to its example country,matching,Africa=Kenya|Asia=Japan|Europe=France|South America=Brazil,Africa=Kenya|Asia=Japan|Europe=France|South America=Brazil
Which planet is known as the Red Planet?,multipleChoice,A) Venus|B) Mars|C) Jupiter|D) Saturn,B
What is the boiling point of water at sea level?,shortAnswer,,100°C
Who painted the Mona Lisa?,shortAnswer,,Leonardo da Vinci
What is the formula for the area of a rectangle?,shortAnswer,,Length × width
Match the animal to its habitat,matching,Polar bear=Arctic|Camel=Desert|Frog=Wetland|Eagle=Mountain,Polar bear=Arctic|Camel=Desert|Frog=Wetland|Eagle=Mountain
What does the acronym CPU stand for?,shortAnswer,,Central Processing Unit
Which gas do plants absorb from the atmosphere?,multipleChoice,A) Oxygen|B) Carbon dioxide|C) Nitrogen|D) Hydrogen,B
Define globalization in one sentence.,essay,,The increasing interconnectedness of countries through trade, communication, and culture.
Match the shape to its property,matching,Triangle=3 sides|Rectangle=4 right angles|Circle=No straight sides,Triangle=3 sides|Rectangle=4 right angles|Circle=No straight sides`;

  // Download CSV Template
  const handleDownloadCSVTemplate = () => {
    const element = document.createElement('a');
    const file = new Blob([csvExampleTemplate], { type: 'text/csv' });
    element.href = URL.createObjectURL(file);
    element.download = 'question_template_example.csv';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success('CSV template downloaded successfully');
  };

  // Handle CSV file upload for questions
  const handleCSVUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const csv = event.target?.result;
        const lines = csv.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          toast.error('CSV must have header row and at least one question');
          return;
        }

        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const questionIndex = headers.indexOf('question');
        const typeIndex = headers.indexOf('type');
        const optionsIndex = headers.indexOf('options');
        const answerIndex = headers.indexOf('answer');

        if (questionIndex === -1) {
          toast.error('CSV must have "question" column');
          return;
        }

        const parsedQuestions = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          if (values[questionIndex]) {
            parsedQuestions.push({
              id: `q_${i}`,
              question: values[questionIndex],
              type: values[typeIndex] || 'essay',
              options: values[optionsIndex] ? values[optionsIndex].split('|') : [],
              answer: values[answerIndex] || '',
            });
          }
        }

        setQuestions(parsedQuestions);
        toast.success(`Loaded ${parsedQuestions.length} questions from CSV`);
      } catch (err) {
        console.error(err);
        toast.error('Error parsing CSV file');
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="teacher-assignments-section">
      <div className="section-header">
        <h2>Manage Assignments</h2>
        <button className="btn btn-primary" onClick={() => {
          resetForm();
          setShowCreateModal(true);
        }}>
          <Plus size={18} />
          Create Assignment
        </button>
      </div>

      {/* Assignments List */}
      <div className="assignments-container">
        {loading ? (
          <div className="loading">Loading assignments...</div>
        ) : assignments.length > 0 ? (
          <div className="assignments-grid">
            {assignments.map(assignment => (
              <div key={assignment.id} className="assignment-card">
                <div className="card-header">
                  <div>
                    <h3>{assignment.title}</h3>
                    <span className={`status-badge ${assignment.status?.toLowerCase()}`}>
                      {assignment.status || 'draft'}
                    </span>
                  </div>
                  <div className="card-actions">
                    {assignment.status === 'draft' && (
                      <button
                        className="action-btn publish"
                        onClick={() => handlePublishAssignment(assignment.id)}
                        title="Publish to Students"
                      >
                        <Upload size={16} />
                      </button>
                    )}
                    <button
                      className="action-btn grade"
                      onClick={() => handleGradeSubmissions(assignment.id)}
                      title="Grade Submissions"
                    >
                      <CheckCircle size={16} />
                    </button>
                    <button
                      className="action-btn stats"
                      onClick={() => handleViewStats(assignment.id)}
                      title="View Statistics"
                    >
                      <ChartBar size={16} />
                    </button>
                    <button
                      className="action-btn edit"
                      onClick={() => handleEditAssignment(assignment)}
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDeleteAssignment(assignment.id)}
                      title="Delete"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </div>

                <div className="card-body">
                  <p className="description">{assignment.description}</p>

                  <div className="meta-info">
                    <div className="meta-item">
                      <span className="label">Class</span>
                      <span className="value">{assignment.class_name}</span>
                    </div>
                    <div className="meta-item">
                      <span className="label">Subject</span>
                      <span className="value">{assignment.subject_name}</span>
                    </div>
                  </div>

                  <div className="meta-info">
                    <div className="meta-item">
                      <span className="label">Due Date</span>
                      <span className="value">{new Date(assignment.due_date).toLocaleDateString()}</span>
                    </div>
                    <div className="meta-item">
                      <span className="label">Points</span>
                      <span className="value">{assignment.total_points}</span>
                    </div>
                  </div>
                </div>

                <div className="card-footer">
                  <button 
                    className="btn btn-small"
                    onClick={() => handleGradeSubmissions(assignment.id)}
                  >
                    View Submissions
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No assignments created yet</p>
            <p className="text-muted">Create your first assignment to get started</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h3>{selectedAssignment ? 'Edit Assignment' : 'Create New Assignment'}</h3>
                {/* Tab Navigation */}
                <div className="modal-tabs">
                  <button
                    className={`tab-button ${modalTab === 'basic' ? 'active' : ''}`}
                    onClick={() => setModalTab('basic')}
                  >
                    Basic Info
                  </button>
                  <button
                    className={`tab-button ${modalTab === 'questions' ? 'active' : ''}`}
                    onClick={() => setModalTab('questions')}
                  >
                    Questions
                  </button>
                  <button
                    className={`tab-button ${modalTab === 'grading' ? 'active' : ''}`}
                    onClick={() => setModalTab('grading')}
                  >
                    Grading
                  </button>
                </div>
              </div>
              <button
                className="modal-close-btn"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleCreateAssignment} className="assignment-form">
              
              {/* Tab 1: Basic Info */}
              {modalTab === 'basic' && (
                <div className="tab-content">
                  {taughtClasses.length === 0 ? (
                    <div style={{
                      padding: '20px',
                      backgroundColor: '#fee2e2',
                      border: '1px solid #fca5a5',
                      borderRadius: '6px',
                      marginBottom: '20px',
                      color: '#991b1b'
                    }}>
                      <p style={{ margin: 0, fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}><Warning size={18} /> No Classes Assigned</p>
                      <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
                        You are not assigned to teach any classes yet. Please contact your school admin to assign you classes before creating assignments.
                      </p>
                    </div>
                  ) : null}
                  <FormSelect
                    label="Class"
                    required
                    value={formData.class_id}
                    onChange={(e) => setFormData({ ...formData, class_id: e.target.value })}
                    options={taughtClasses.map(c => ({
                      value: c.class_id,
                      label: `${c.class_name} - ${c.subject_name}`,
                    }))}
                  />

                  <FormSelect
                    label="Assignment Type"
                    value={formData.assignment_type}
                    onChange={(e) => setFormData({ ...formData, assignment_type: e.target.value })}
                    options={[
                      { value: 'classwork', label: 'Classwork (CA1)' },
                      { value: 'homework', label: 'Homework (CA2)' },
                      { value: 'quiz', label: 'Quiz (CA3)' },
                      { value: 'project', label: 'Project (CA4)' },
                      { value: 'worksheet', label: 'Worksheet' },
                    ]}
                  />

                  <FormInput
                    label="Title"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Chapter 5 Algebra Problems"
                  />

                  <FormInput
                    label="Description"
                    as="textarea"
                    rows="3"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the assignment"
                  />

                  <FormInput
                    label="Instructions"
                    as="textarea"
                    rows="3"
                    value={formData.instructions}
                    onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                    placeholder="Provide detailed instructions"
                  />

                  <FormInput
                    label="Due Date"
                    type="date"
                    required
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  />

                  <FormInput
                    label="Total Points"
                    type="number"
                    step="1"
                    min="1"
                    value={formData.total_points}
                    onChange={(e) => setFormData({ ...formData, total_points: e.target.value })}
                  />
                </div>
              )}

              {/* Tab 2: Upload Questions */}
              {modalTab === 'questions' && (
                <div className="tab-content">
                  <div className="upload-section">
                    <h4>Upload Questions from CSV</h4>
                    <div className="csv-template">
                      <p className="small-text">CSV Format: question, type, options (pipe-separated), answer</p>
                      <p className="small-text">Types: essay, multipleChoice, shortAnswer, matching</p>
                      <button
                        type="button"
                        className="download-template-btn"
                        onClick={handleDownloadCSVTemplate}
                        style={{
                          marginTop: '8px',
                          padding: '8px 16px',
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          transition: 'background-color 0.2s',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <DownloadSimple size={18} /> Download Example Template
                      </button>
                    </div>
                    <div className="file-input-wrapper">
                      <input
                        id="csv-file-input"
                        type="file"
                        accept=".csv"
                        onChange={handleCSVUpload}
                        className="file-input"
                        style={{ display: 'none' }}
                      />
                      <label 
                        htmlFor="csv-file-input"
                        className="file-label"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '10px 16px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          transition: 'background-color 0.2s',
                          marginTop: '12px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                      >
                        <FolderOpen size={18} /> Choose CSV File
                      </label>
                    </div>
                  </div>

                  {questions.length > 0 && (
                    <div className="questions-preview">
                      <h4>Loaded Questions ({questions.length})</h4>
                      <div className="questions-list">
                        {questions.map((q, idx) => (
                          <div key={q.id} className="question-item">
                            <span className="question-number">Q{idx + 1}</span>
                            <div className="question-info">
                              <p className="question-text">{q.question}</p>
                              <span className="question-type">{q.type}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setQuestions(questions.filter((_, i) => i !== idx))}
                              className="remove-btn"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tab 3: Grading Settings */}
              {modalTab === 'grading' && (
                <div className="tab-content">
                  <div className="grading-section">
                    <h4>Grading Options</h4>
                    
                    <div className="grading-option">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={gradingSettings.allow_auto_grade}
                          onChange={(e) => setGradingSettings({
                            ...gradingSettings,
                            allow_auto_grade: e.target.checked
                          })}
                        />
                        <span>Allow Auto-Grading (for multiple choice & short answer)</span>
                      </label>
                    </div>

                    <div className="grading-option">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={gradingSettings.rubric_enabled}
                          onChange={(e) => setGradingSettings({
                            ...gradingSettings,
                            rubric_enabled: e.target.checked
                          })}
                        />
                        <span>Enable Rubric-Based Grading (for essays)</span>
                      </label>
                    </div>

                    <div className="question-types">
                      <h5>Supported Question Types</h5>
                      <div className="type-list">
                        {['essay', 'multipleChoice', 'shortAnswer', 'matching'].map(type => (
                          <label key={type} className="type-checkbox">
                            <input
                              type="checkbox"
                              checked={gradingSettings.question_types.includes(type)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setGradingSettings({
                                    ...gradingSettings,
                                    question_types: [...gradingSettings.question_types, type]
                                  });
                                } else {
                                  setGradingSettings({
                                    ...gradingSettings,
                                    question_types: gradingSettings.question_types.filter(t => t !== type)
                                  });
                                }
                              }}
                            />
                            <span>{type === 'multipleChoice' ? 'Multiple Choice' : type.charAt(0).toUpperCase() + type.slice(1)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {selectedAssignment ? 'Update Assignment' : 'Create Assignment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Statistics Modal */}
      {showStatsModal && stats && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Assignment Statistics</h3>
              <button
                className="modal-close-btn"
                onClick={() => {
                  setShowStatsModal(false);
                  setStats(null);
                }}
              >
                <X size={24} />
              </button>
            </div>
            <div className="stats-container">
            <div className="stat-card">
              <span className="stat-value">{stats.total_students || 0}</span>
              <span className="stat-label">Total Students</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats.submitted || 0}</span>
              <span className="stat-label">Submitted</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats.graded || 0}</span>
              <span className="stat-label">Graded</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats.missing || 0}</span>
              <span className="stat-label">Missing</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats.late || 0}</span>
              <span className="stat-label">Late</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{(stats.average_score || 0).toFixed(1)}</span>
              <span className="stat-label">Average Score</span>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Submission Grading Panel */}
      {showGradingPanel && selectedAssignment && (
        <SubmissionGradingPanel
          assignmentId={selectedAssignment.id}
          submissions={submissions}
          onClose={() => {
            setShowGradingPanel(false);
            setSelectedAssignment(null);
            setSubmissions([]);
          }}
          onGradingComplete={() => {
            loadAssignments(); // Reload assignments to update stats
            // Reload submissions if grading panel is still open
            if (selectedAssignment) {
              handleGradeSubmissions(selectedAssignment.id);
            }
          }}
        />
      )}
    </div>
  );
}

export default TeacherAssignmentsSection;
